import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, BookOpen, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminScriptures = () => {
  const [scriptures, setScriptures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    verse: '',
    reference: '',
    reflection: '',
    active_from: '',
    active_until: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchScriptures();
  }, []);

  const fetchScriptures = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/scriptures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScriptures(response.data);
    } catch (error) {
      console.error('Failed to fetch scriptures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.verse || !formData.reference) {
      toast.error('Verse and reference are required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/scripture`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Soul Food created successfully');
      setShowDialog(false);
      setFormData({ verse: '', reference: '', reflection: '', active_from: '', active_until: '' });
      fetchScriptures();
    } catch (error) {
      toast.error('Failed to create soul food');
    } finally {
      setSaving(false);
    }
  };

  const isActive = (scripture) => {
    const now = new Date().toISOString();
    if (!scripture.active_from) return true;
    if (scripture.active_from <= now && (!scripture.active_until || scripture.active_until >= now)) {
      return true;
    }
    return false;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold text-forest">Weekly Soul Food</h2>
          <p className="text-gray-600">Manage the weekly scripture and reflections for members.</p>
        </div>
        <Button className="btn-primary" onClick={() => setShowDialog(true)}>
          <Plus className="h-4 w-4 mr-2" /> Add Soul Food
        </Button>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />
          ))}
        </div>
      ) : scriptures.length === 0 ? (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-forest mb-2">No Soul Food Yet</h3>
            <p className="text-gray-500 mb-4">Add weekly scriptures for members to reflect on.</p>
            <Button className="btn-primary" onClick={() => setShowDialog(true)}>
              Add First Soul Food
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {scriptures.map((scripture) => (
            <Card key={scripture.id} className="card-base">
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-heading text-lg font-semibold text-forest">
                        {scripture.reference}
                      </h3>
                      {isActive(scripture) && (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      )}
                    </div>
                    <p className="text-gray-700 italic mb-3">"{scripture.verse}"</p>
                    {scripture.reflection && (
                      <p className="text-sm text-gray-500">{scripture.reflection}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Added: {new Date(scripture.created_at).toLocaleDateString()}
                      </span>
                      {scripture.active_from && (
                        <span>From: {new Date(scripture.active_from).toLocaleDateString()}</span>
                      )}
                      {scripture.active_until && (
                        <span>Until: {new Date(scripture.active_until).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Soul Food Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-forest">
              Add Weekly Soul Food
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="verse">Soul Food Verse *</Label>
              <Textarea
                id="verse"
                value={formData.verse}
                onChange={(e) => setFormData({ ...formData, verse: e.target.value })}
                placeholder="Enter the scripture verse..."
                className="input-base min-h-24"
              />
            </div>

            <div>
              <Label htmlFor="reference">Reference *</Label>
              <Input
                id="reference"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="e.g., Jeremiah 29:11"
                className="input-base"
              />
            </div>

            <div>
              <Label htmlFor="reflection">Reflection (Optional)</Label>
              <Textarea
                id="reflection"
                value={formData.reflection}
                onChange={(e) => setFormData({ ...formData, reflection: e.target.value })}
                placeholder="This week, we stand together on this promise..."
                className="input-base"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="active_from">Active From</Label>
                <Input
                  id="active_from"
                  type="date"
                  value={formData.active_from}
                  onChange={(e) => setFormData({ ...formData, active_from: e.target.value })}
                  className="input-base"
                />
              </div>
              <div>
                <Label htmlFor="active_until">Active Until</Label>
                <Input
                  id="active_until"
                  type="date"
                  value={formData.active_until}
                  onChange={(e) => setFormData({ ...formData, active_until: e.target.value })}
                  className="input-base"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Add Soul Food'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminScriptures;
